import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue, useSetRecoilState } from "recoil";

import {
  castErrorResult,
  companyIdSelector,
  createCatchErrorResult,
  createErrorResult,
  errors,
  IProduceInitResultData,
  IProduceJoinDto,
  Log,
  ProduceJoinApi,
  productionLogStateAtom,
  productionPollGroupStateAtom,
  SimpleResult,
  simpleResultErrorEmptyResponse,
  simpleResultInvalidApiResponse,
} from "@ryde/shared";

import { useProducePollingContext } from "../../contexts";

export const useProducePollingActions = () => {
  const { t } = useTranslation();
  const setProductionGroupState = useSetRecoilState(
    productionPollGroupStateAtom,
  );
  const companyId = useRecoilValue(companyIdSelector);
  const setLogState = useSetRecoilState(productionLogStateAtom);
  const { initProducePolling, stopAndCleanupProducePolling } =
    useProducePollingContext();

  const produceJoinStart = useEvent(
    async (
      payload: IProduceJoinDto,
    ): Promise<SimpleResult<IProduceInitResultData>> => {
      try {
        Log.debug("Init produce for data:", payload);

        const stopPreviousResult = await stopAndCleanupProducePolling();

        if (!stopPreviousResult.success) {
          return castErrorResult(stopPreviousResult);
        }

        const apiPayload: IProduceJoinDto = { ...payload, companyId };
        const produceStartResult =
          await ProduceJoinApi.produceStart(apiPayload);

        if (!produceStartResult) {
          return simpleResultErrorEmptyResponse(t);
        }

        if (!produceStartResult.success) {
          return produceStartResult;
        }

        if (!produceStartResult.data) {
          return simpleResultInvalidApiResponse(t);
        }

        const { productionIds, productionGroupId, states, chains } =
          produceStartResult.data;
        const productionId = productionIds[0];

        if (!productionId) {
          return createErrorResult(
            errors.produce.missingToken,
            "Missing production ID",
          );
        }

        setLogState(undefined);
        const nextGroupState = {
          productionGroupId,
          productionIds,
          states,
          chains,
          productionPollId: productionId,
          productionUiId: productionId,
        };
        setProductionGroupState(nextGroupState);

        const initPollingResult = initProducePolling(nextGroupState);

        if (!initPollingResult.success) {
          return castErrorResult(initPollingResult);
        }

        return produceStartResult;
      } catch (error) {
        Log.warn(t("errors.failedToProduceJoin"), error);
        return createCatchErrorResult(error, t);
      }
    },
  );

  return { produceJoinStart };
};
